import { z } from "zod";
import { apiClient } from "./api";
import { assetLocatorSchema } from "./asset-locator";

export const REPORT_SCHEMA_VERSION = 1 as const;
export const REPORT_SCHEMA_VERSION_V2 = 2 as const;
export const MAX_REPORT_REVISIONS = 100;
export const MAX_REPORT_BLOCKS = 40;
export const MAX_REPORT_SOURCES = 100;
export const MAX_REPORT_TITLE_LENGTH = 200;
export const MAX_REPORT_HEADING_LENGTH = 200;
export const MAX_REPORT_TEXT_CHARACTERS = 250_000;
export const MAX_REPORT_SERIALIZED_BYTES = 1_048_576;
export const MAX_CHART_SERIES = 8;
export const MAX_CHART_POINTS = 800;
export const MAX_TABLE_COLUMNS = 20;
export const MAX_TABLE_ROWS = 200;
export const MAX_TABLE_CELLS = 4_000;
export const MAX_DIAGRAM_NODES = 20;
export const MAX_DIAGRAM_EDGES = 30;
export const MAX_REPORT_ASSETS = 12;
export const MAX_IMAGE_BLOCK_ASSETS = 3;

function codePointBoundedString(minimum: number, maximum: number) {
  return z.string().refine((value) => {
    const codePointLength = Array.from(value).length;
    return codePointLength >= minimum && codePointLength <= maximum;
  });
}

const identifierSchema = codePointBoundedString(1, 100);
// Opaque handle issued by the trusted asset registry. The character class
// mirrors the backend's `ReportAssetHandle` and rules out URLs, storage keys,
// base64/data URIs, HTML/SVG markup, and Markdown image syntax by
// construction: none of those can match this pattern.
const reportAssetHandleSchema = z.string().regex(/^[A-Za-z0-9_-]{16,64}$/);
const reportTitleSchema = codePointBoundedString(1, MAX_REPORT_TITLE_LENGTH);
const reportSubtitleSchema = codePointBoundedString(0, MAX_REPORT_TITLE_LENGTH);
const headingSchema = codePointBoundedString(1, MAX_REPORT_HEADING_LENGTH);
const labelSchema = codePointBoundedString(1, 200);
const unitSchema = codePointBoundedString(1, 100);
const languageSchema = codePointBoundedString(2, 35);
const generationRationaleSchema = codePointBoundedString(1, 2_000);
const finiteNumberSchema = z.number().finite();
const revisionNumberSchema = finiteNumberSchema.int().min(1).max(MAX_REPORT_REVISIONS);
const isoDateSchema = z.string().date();
const strictScalarSchema = z.union([z.string(), finiteNumberSchema, z.boolean(), z.null()]);

export type StrictScalar = z.infer<typeof strictScalarSchema>;

export const reportPointerSchema = z
  .object({
    report_id: z.guid(),
    revision_number: revisionNumberSchema,
  })
  .strict();

export type ReportPointer = z.infer<typeof reportPointerSchema>;

export const reportRefSchema = reportPointerSchema
  .extend({
    title: reportTitleSchema,
    status: z.enum(["ready", "partial"]).optional(),
  })
  .strict();

export type ReportRef = z.infer<typeof reportRefSchema>;

export const evidenceBindingSchema = z
  .object({
    support: z.enum(["direct", "inference"]),
    source_handles: z.array(z.string()).min(1).max(MAX_REPORT_SOURCES),
  })
  .strict();

export type EvidenceBinding = z.infer<typeof evidenceBindingSchema>;

export const reportSourceHandleSchema = z
  .object({
    handle: identifierSchema,
  })
  .strict();

export type ReportSourceHandle = z.infer<typeof reportSourceHandleSchema>;

export const reportGenerationMetadataSchema = z
  .object({
    rationale: generationRationaleSchema,
    confidence: finiteNumberSchema.min(0).max(1),
  })
  .strict();

export type ReportGenerationMetadata = z.infer<typeof reportGenerationMetadataSchema>;

export const reportClaimSchema = z
  .object({
    text: z.string().min(1),
    evidence: evidenceBindingSchema,
  })
  .strict();

export type ReportClaim = z.infer<typeof reportClaimSchema>;

export const executiveSummaryBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("executive_summary"),
    heading: headingSchema,
    thesis: reportClaimSchema,
    highlights: z.array(reportClaimSchema).min(1).max(5),
  })
  .strict();

export type ExecutiveSummaryBlock = z.infer<typeof executiveSummaryBlockSchema>;

export const kpiItemSchema = z
  .object({
    metric_id: identifierSchema,
    label: labelSchema,
    value: strictScalarSchema,
    unit: unitSchema.nullable().optional(),
    delta: strictScalarSchema.optional(),
    period: unitSchema.nullable().optional(),
    evidence: evidenceBindingSchema.nullable().optional(),
  })
  .strict();

export type KpiItem = z.infer<typeof kpiItemSchema>;

export const kpiGridBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("kpi_grid"),
    heading: headingSchema,
    evidence: evidenceBindingSchema.nullable().optional(),
    items: z.array(kpiItemSchema).min(1).max(12),
  })
  .strict();

export type KpiGridBlock = z.infer<typeof kpiGridBlockSchema>;

export const chartPointSchema = z
  .object({
    label: labelSchema,
    value: finiteNumberSchema,
  })
  .strict();

export type ChartPoint = z.infer<typeof chartPointSchema>;

export const chartSeriesSchema = z
  .object({
    series_id: identifierSchema,
    label: labelSchema,
    unit: unitSchema.nullable().optional(),
    points: z.array(chartPointSchema).min(1).max(MAX_CHART_POINTS),
    evidence: evidenceBindingSchema.nullable().optional(),
  })
  .strict();

export type ChartSeries = z.infer<typeof chartSeriesSchema>;

const chartSeriesListSchema = z
  .array(chartSeriesSchema)
  .min(1)
  .max(MAX_CHART_SERIES)
  .refine(
    (series) => series.reduce((total, item) => total + item.points.length, 0) <= MAX_CHART_POINTS,
  );

export const chartBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("chart"),
    heading: headingSchema,
    evidence: evidenceBindingSchema.nullable().optional(),
    chart_kind: z.enum(["bar", "line", "area", "donut"]),
    x_axis_label: labelSchema.nullable().optional(),
    y_axis_label: labelSchema.nullable().optional(),
    unit: unitSchema.nullable().optional(),
    series: chartSeriesListSchema,
    takeaway: reportClaimSchema,
  })
  .strict();

export type ChartBlock = z.infer<typeof chartBlockSchema>;

export const diagramNodeSchema = z
  .object({
    node_id: identifierSchema,
    label: labelSchema,
    group_id: identifierSchema.nullable().optional(),
  })
  .strict();

export type DiagramNode = z.infer<typeof diagramNodeSchema>;

export const diagramEdgeSchema = z
  .object({
    edge_id: identifierSchema,
    source_node_id: identifierSchema,
    target_node_id: identifierSchema,
    label: labelSchema.nullable().optional(),
  })
  .strict();

export type DiagramEdge = z.infer<typeof diagramEdgeSchema>;

export const diagramGroupSchema = z
  .object({
    group_id: identifierSchema,
    label: labelSchema,
  })
  .strict();

export type DiagramGroup = z.infer<typeof diagramGroupSchema>;

export const diagramBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("diagram"),
    heading: headingSchema,
    diagram_kind: z.enum(["flow", "timeline", "hierarchy", "relationship"]),
    direction: z.enum(["TB", "BT", "LR", "RL"]),
    nodes: z.array(diagramNodeSchema).min(1).max(MAX_DIAGRAM_NODES),
    edges: z.array(diagramEdgeSchema).max(MAX_DIAGRAM_EDGES),
    groups: z.array(diagramGroupSchema).max(MAX_DIAGRAM_NODES),
    explanation: reportClaimSchema,
  })
  .strict();

export type DiagramBlock = z.infer<typeof diagramBlockSchema>;

export const tableColumnSchema = z
  .object({
    column_id: identifierSchema,
    label: labelSchema,
    data_type: z.enum(["text", "number", "date", "boolean"]),
    unit: unitSchema.nullable().optional(),
  })
  .strict();

export type TableColumn = z.infer<typeof tableColumnSchema>;

export const tableRowSchema = z
  .object({
    row_id: identifierSchema,
    cells: z
      .record(z.string(), strictScalarSchema)
      .refine((cells) => Object.keys(cells).length <= MAX_TABLE_COLUMNS),
    evidence: evidenceBindingSchema.nullable().optional(),
  })
  .strict();

export type TableRow = z.infer<typeof tableRowSchema>;

export const tableBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("table"),
    heading: headingSchema,
    evidence: evidenceBindingSchema.nullable().optional(),
    columns: z.array(tableColumnSchema).min(1).max(MAX_TABLE_COLUMNS),
    rows: z.array(tableRowSchema).max(MAX_TABLE_ROWS),
    takeaway: reportClaimSchema.nullable().optional(),
  })
  .strict();

export type TableBlock = z.infer<typeof tableBlockSchema>;

export const markdownSegmentSchema = z
  .object({
    segment_id: identifierSchema,
    markdown: z.string().min(1),
    evidence: evidenceBindingSchema,
  })
  .strict();

export type MarkdownSegment = z.infer<typeof markdownSegmentSchema>;

export const narrativeBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("narrative"),
    heading: headingSchema,
    segments: z.array(markdownSegmentSchema).min(1),
  })
  .strict();

export type NarrativeBlock = z.infer<typeof narrativeBlockSchema>;

export const appendixBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("appendix"),
    heading: headingSchema,
    segments: z.array(markdownSegmentSchema).min(1),
  })
  .strict();

export type AppendixBlock = z.infer<typeof appendixBlockSchema>;

export const calloutBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("callout"),
    heading: headingSchema,
    intent: z.enum(["insight", "risk", "decision", "note"]),
    content: reportClaimSchema,
  })
  .strict();

export type CalloutBlock = z.infer<typeof calloutBlockSchema>;

export const recommendationItemSchema = z
  .object({
    item_id: identifierSchema,
    action: z.string().min(1),
    rationale: z.string().min(1),
    priority: z.enum(["low", "medium", "high", "critical"]),
    owner: labelSchema.nullable().optional(),
    horizon: labelSchema.nullable().optional(),
    evidence: evidenceBindingSchema,
  })
  .strict();

export type RecommendationItem = z.infer<typeof recommendationItemSchema>;

export const recommendationsBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("recommendations"),
    heading: headingSchema,
    items: z.array(recommendationItemSchema).min(1),
  })
  .strict();

export type RecommendationsBlock = z.infer<typeof recommendationsBlockSchema>;

// The strict, legacy schema-v1 block union (9 kinds, no `image`). This is the
// exact original `reportBlockSchema` shape, renamed so v1 definitions/patches
// can keep referencing it unchanged while the exported `reportBlockSchema`
// below grows to include `image` for schema v2 and for rendering.
export const reportBlockV1Schema = z.discriminatedUnion("kind", [
  executiveSummaryBlockSchema,
  kpiGridBlockSchema,
  chartBlockSchema,
  diagramBlockSchema,
  tableBlockSchema,
  narrativeBlockSchema,
  calloutBlockSchema,
  recommendationsBlockSchema,
  appendixBlockSchema,
]);

export type ReportBlockV1 = z.infer<typeof reportBlockV1Schema>;

export const imageAssetRefSchema = z
  .object({
    handle: reportAssetHandleSchema,
    evidence: evidenceBindingSchema.nullable().optional(),
  })
  .strict();

export type ImageAssetRef = z.infer<typeof imageAssetRefSchema>;

export const imageBlockSchema = z
  .object({
    block_id: identifierSchema,
    kind: z.literal("image"),
    heading: headingSchema,
    behavior: z.enum(["required", "optional"]),
    presentation: z.enum(["inline", "wide", "gallery"]),
    evidence: evidenceBindingSchema.nullable().optional(),
    assets: z.array(imageAssetRefSchema).min(1).max(MAX_IMAGE_BLOCK_ASSETS),
  })
  .strict();

export type ImageBlock = z.infer<typeof imageBlockSchema>;

// ---------------------------------------------------------------------------
// Report visual relations (PR 4, Task 5/6 contract fixup) — the
// SERVER-RESOLVED projection for the assets referenced by every `image`
// block in a revision.
//
// The wire shape below is deliberately the EXACT shape the backend emits —
// `SafeVisualRelationProjection` (`backend/src/knowledge_assets/
// visual_projection.py`), reused unchanged as `SafeReportProjection.
// visual_assets` (`backend/src/reports/projections.py`): `relation_id`,
// `included`, and `citation_label`/`alt_text`/`caption` that are only ever
// populated when `included` is true. There is NO `handle` field anywhere in
// that backend type, by design (module docstring: chat and report images
// share this one projection primitive; a chat turn's assets and a report's
// assets are keyed by `chat_message_assets`/`report_revision_assets` rows,
// never by the model-authored opaque handle that only ever existed to let
// the model *reference* an asset during generation). `visual_assets` is a
// FLAT array ordered by `report_revision_assets.(block_id, position)` — the
// backend never re-keys it per block.
//
// CORRELATION DECISION: given `visual_assets` carries no handle and cannot
// gain one without touching the shared `knowledge_assets` DTO (used by both
// reports and chat turn sharing), the link back to "which asset ref, in
// which block" is made explicit on THIS side instead, by reproducing the
// exact grouping algorithm the PDF renderer harness already implements for
// the identical problem (`report_renderer/harness/print.html`'s
// `assignVisualRelationsByBlock`, itself matching the SQL ordering in
// `ReportPdfService._visual_facts_for_revision` / `ReportSharingService.
// _visual_facts_for_revision`): sort the definition's `image` blocks by
// `block_id` (the same key the backend query orders by), then walk that
// sorted block list slicing `visual_assets` sequentially — block N's slice
// has exactly `block.assets.length` entries because a kept `image` block is
// always resolved all-or-nothing (`publication.py::_resolve_report_assets`
// omits a whole optional block rather than a partial asset list within it),
// so the counts always add up. `groupReportVisualRelationsByBlock` below is
// that algorithm; every render surface must group once (per revision/
// projection) and pass each block its own slice — never search the flat
// array by content.
//
// The frontend still keeps a stronger type-level guarantee than the wire
// shape does: `toReportImageRelation` below normalizes the loosely-typed
// wire entry into a real two-state discriminated union so a redacted
// relation is STRUCTURALLY incapable of carrying alt text, a caption, or a
// citation label, regardless of what a buggy/malicious backend response
// might include alongside `included: false`.
//
// `locator`/`original_available` (return-knowledge-images-across-surfaces
// PR 4 gap closure #2) mirror that same "only when included" contract:
// `locator` is trusted POSITION metadata only (page/block/timestamp — see
// `AssetLocator` in `./asset-locator`), never a filename or storage path,
// so it is safe even for an anonymous view; `original_available` is a
// best-effort UI signal ("does this specific audience's projection offer a
// download"), always `false` for a public relation regardless of the
// underlying asset (`project_public_visual_relation` hardcodes it). Both
// default when absent so a stored payload from before this field existed
// (or a caller — `ReportPdfService`/`ReportSharingService`'s narrower
// facts-builders — that has not been updated to populate it) still parses.
export const safeVisualRelationProjectionSchema = z
  .object({
    relation_id: z.guid(),
    included: z.boolean(),
    citation_label: codePointBoundedString(1, 32).nullable(),
    alt_text: codePointBoundedString(1, 500).nullable(),
    caption: codePointBoundedString(0, 4_000).nullable(),
    locator: assetLocatorSchema.nullable().default(null),
    original_available: z.boolean().default(false),
  })
  .strict();

export type SafeVisualRelationProjection = z.infer<typeof safeVisualRelationProjectionSchema>;

const visualAssetListSchema = z
  .array(safeVisualRelationProjectionSchema)
  .max(MAX_REPORT_ASSETS)
  .default([]);

// The frontend-only, strictly narrower rendering type: "redacted" cannot
// carry any of the available-only fields, by construction of the type
// itself (not merely by convention of what a well-behaved backend sends).
// `locator` stays nullable even here: not every producer of an "included"
// relation has been updated to populate it yet (see the module comment
// above), so `null` means "no trusted locator for this relation from THIS
// response", not "redacted" — the redacted branch is a wholly separate
// schema variant below that cannot carry the field at all.
export const reportImageRelationAvailableSchema = z
  .object({
    state: z.literal("available"),
    relation_id: z.guid(),
    alt_text: codePointBoundedString(1, 500),
    caption: codePointBoundedString(0, 4_000).nullable(),
    citation_label: codePointBoundedString(1, 32).nullable(),
    locator: assetLocatorSchema.nullable(),
    original_available: z.boolean(),
  })
  .strict();

export type ReportImageRelationAvailable = z.infer<typeof reportImageRelationAvailableSchema>;

export const reportImageRelationRedactedSchema = z
  .object({
    state: z.literal("redacted"),
    relation_id: z.guid(),
  })
  .strict();

export const reportImageRelationSchema = z.discriminatedUnion("state", [
  reportImageRelationAvailableSchema,
  reportImageRelationRedactedSchema,
]);

export type ReportImageRelation = z.infer<typeof reportImageRelationSchema>;

/**
 * Normalize one wire-shaped `SafeVisualRelationProjection` entry into the
 * strict two-state union. `included && alt_text` both hold or neither does
 * for any real backend response (`project_owner_visual_relation`/
 * `project_team_visual_relation`/`project_team_minimum_visual_relation`/
 * `project_public_visual_relation` never produce `included: true` without
 * `alt_text`) — the `raw.included && raw.alt_text` guard is defense in
 * depth against a malformed response, not a second tolerated wire shape.
 */
export function toReportImageRelation(raw: SafeVisualRelationProjection): ReportImageRelation {
  if (raw.included && raw.alt_text) {
    return {
      state: "available",
      relation_id: raw.relation_id,
      alt_text: raw.alt_text,
      caption: raw.caption,
      citation_label: raw.citation_label,
      locator: raw.locator,
      original_available: raw.original_available,
    };
  }
  return { state: "redacted", relation_id: raw.relation_id };
}

/**
 * Groups the flat `visual_assets` projection array back into per-block
 * relation lists, keyed by `block_id`. See the module comment above for why
 * this exact "sort by block_id, slice sequentially" algorithm (mirroring
 * `report_renderer/harness/print.html`'s `assignVisualRelationsByBlock`) is
 * the correlation contract, not a `handle` field.
 */
export function groupReportVisualRelationsByBlock(
  definition: ReportDefinition,
  visualAssets: readonly SafeVisualRelationProjection[],
): ReadonlyMap<string, ReportImageRelation[]> {
  // `definition.blocks` is typed as a union of the v1/v2 block-array types
  // (the two `ReportDefinition` variants disagree on `blocks`'s element
  // type) — widen to the superset `ReportBlock[]` first so `.filter`'s type
  // predicate narrows normally instead of losing the guard across the union.
  const blocks: readonly ReportBlock[] = definition.blocks;
  const imageBlocks = blocks.filter((block): block is ImageBlock => block.kind === "image");
  const sortedBlockIds = imageBlocks.map((block) => block.block_id).sort();
  const blocksById = new Map(imageBlocks.map((block) => [block.block_id, block] as const));
  const byBlockId = new Map<string, ReportImageRelation[]>();
  let cursor = 0;
  for (const blockId of sortedBlockIds) {
    const block = blocksById.get(blockId);
    const count = block?.assets.length ?? 0;
    byBlockId.set(blockId, visualAssets.slice(cursor, cursor + count).map(toReportImageRelation));
    cursor += count;
  }
  return byBlockId;
}

/**
 * Byte-fetching interface injected into report image rendering
 * (`ReportImage.tsx`), mirroring `useAuthedBlobUrl`'s `fetchBlob` shape.
 * Each surface (owner dock/expanded via `ReportReader`, the authenticated
 * team standalone view, the anonymous public standalone view) must supply
 * its OWN implementation, because the three audiences authorize and
 * transport bytes differently (Bearer token + session/report ids for the
 * owner; a team session for `beflow_team`; a capability in a POST body with
 * `credentials: "omit"` for anonymous — see `publicPdfPost` above for that
 * exact convention). There is no single fetcher that works for all three.
 *
 * `kind: "original"` exists in the transport contract (the owner/team
 * routes accept it) and `ReportImage.tsx` now requests it when
 * `ReportImageRelationAvailable.original_available` is `true` (`return-
 * knowledge-images-across-surfaces` PR 4 gap closure #2 — the shared
 * `SafeVisualRelationProjection` DTO gained that trusted signal). It is a
 * best-effort UI hint only: the byte route independently re-derives and
 * enforces the real download authorization (`AssetAuthorizationService.
 * authorize`) before ever serving a byte.
 */
export type ReportImageByteKind = "rendition" | "original";
export type ReportImageBlobFetcher = (
  relationId: string,
  kind: ReportImageByteKind,
) => Promise<Blob>;

/**
 * Fallback fetcher used only as `ExecutiveReport`/`ReportBlock`'s default
 * prop value — a caller that forgets to pass one gets a fetcher that
 * deliberately never guesses a URL and rejects immediately, which
 * `useAuthedBlobUrl` (via `ReportImage.tsx`) turns into the same neutral
 * "unavailable" tile a real fetch failure would produce. Every real render
 * surface (`ReportReader.tsx`, `shared-report.tsx`, `shared-team-report.tsx`)
 * passes one of the three audience-specific fetchers below instead
 * (`return-knowledge-images-across-surfaces` PR 4, Task 6).
 */
export async function reportImageFetcherNotWired(): Promise<Blob> {
  throw new Error("report_image_fetcher_not_wired");
}

/**
 * Owner fetcher: `POST {shareBase}/asset`, Bearer auth, `{relation_id,
 * variant}` body — mirrors `downloadOwnerPdfBlob`'s auth convention and the
 * team fetcher below's body shape. The owner route
 * (`owner_report_asset` in `src.api.report_shares`) never requires
 * `Capability.VIEW_SOURCE`: an owner viewing their own generated report is
 * never gated by a grant-based permission anywhere else in `owner_router`.
 */
export function createOwnerReportImageFetcher(args: {
  sessionId: string;
  reportId: string;
  revisionNumber: number;
}): ReportImageBlobFetcher {
  return async (relationId, kind) => {
    const { authHeader } = await import("./api");
    const headers: Record<string, string> = {
      Accept: "image/*",
      "Content-Type": "application/json",
      ...(await authHeader()),
    };
    const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
    const path = `${shareBase(args.sessionId, args.reportId, args.revisionNumber)}/asset`;
    const response = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ relation_id: relationId, variant: kind }),
    });
    if (!response.ok) {
      throw new Error(`report_image_fetch_failed_${response.status}`);
    }
    return response.blob();
  };
}

/**
 * Team fetcher: `POST /shared/team/reports/{reportId}/revisions/{n}/asset`,
 * Bearer auth, `{relation_id, variant}` body. The server re-resolves the
 * caller's active `beflow_team` grant and per-recipient authorization on
 * every call — this fetcher carries no capability, only the relation id.
 */
export function createTeamReportImageFetcher(args: {
  reportId: string;
  revisionNumber: number;
}): ReportImageBlobFetcher {
  return async (relationId, kind) => {
    const { authHeader } = await import("./api");
    const headers: Record<string, string> = {
      Accept: "image/*",
      "Content-Type": "application/json",
      ...(await authHeader()),
    };
    const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
    const path = `/shared/team/reports/${encodeURIComponent(args.reportId)}/revisions/${args.revisionNumber}/asset`;
    const response = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ relation_id: relationId, variant: kind }),
    });
    if (!response.ok) {
      throw new Error(`report_image_fetch_failed_${response.status}`);
    }
    return response.blob();
  };
}

/**
 * Public fetcher: capability-in-body convention (never a query string —
 * `publicPdfPost`'s exact posture below), `credentials: "omit"`,
 * `cache: "no-store"`. `capabilities` is `PublicResolveResponse.
 * visual_read_capabilities` — emitted only for relations whose projection
 * entry has `included: true`, so a relation missing from this list has no
 * capability to replay and always resolves to "unavailable" here, matching
 * the redacted tile it already renders as. Public views never offer
 * `kind: "original"` (spec: "Public views SHALL not expose original
 * download").
 */
export function createPublicReportImageFetcher(
  capabilities: readonly PublicVisualReadCapability[],
): ReportImageBlobFetcher {
  return async (relationId, kind) => {
    if (kind !== "rendition") {
      throw new Error("report_image_original_not_available_publicly");
    }
    const capability = capabilities.find((entry) => entry.relation_id === relationId);
    if (!capability) {
      throw new Error("report_image_capability_not_found");
    }
    const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
    const response = await fetch(`${baseUrl}/shared/reports/asset`, {
      method: "POST",
      headers: { Accept: "image/*", "Content-Type": "application/json" },
      body: JSON.stringify({
        grant_id: capability.grant_id,
        relation_id: capability.relation_id,
        projection_hash: capability.projection_hash,
        policy_version: capability.policy_version,
        rendition_identity: capability.rendition_identity,
      }),
      credentials: "omit",
      cache: "no-store",
    });
    if (!response.ok) {
      throw new Error(`report_image_fetch_failed_${response.status}`);
    }
    return response.blob();
  };
}

// The schema-v2 block union: every v1 kind plus `image`. This is also the
// general-purpose "current" block union used for rendering (`ReportBlock.tsx`
// renders whichever blocks a definition — v1 or v2 — actually contains).
export const reportBlockSchema = z.discriminatedUnion("kind", [
  ...reportBlockV1Schema.options,
  imageBlockSchema,
]);

export type ReportBlock = z.infer<typeof reportBlockSchema>;

// Kept as an explicit alias so v2-specific call sites can name their intent;
// it is the same union as the exported `reportBlockSchema`/`ReportBlock`.
export const reportBlockV2Schema = reportBlockSchema;
export type ReportBlockV2 = ReportBlock;

const reportDefinitionV1Schema = z
  .object({
    schema_version: z.literal(REPORT_SCHEMA_VERSION),
    title: reportTitleSchema,
    subtitle: reportSubtitleSchema.nullable().optional(),
    depth: z.enum(["brief", "standard", "deep"]),
    language: languageSchema,
    as_of: isoDateSchema.nullable().optional(),
    sources: z.array(reportSourceHandleSchema).max(MAX_REPORT_SOURCES),
    blocks: z.array(reportBlockV1Schema).min(1).max(MAX_REPORT_BLOCKS),
    generation_metadata: reportGenerationMetadataSchema.nullable().optional(),
  })
  .strict();

const reportDefinitionV2Schema = z
  .object({
    schema_version: z.literal(REPORT_SCHEMA_VERSION_V2),
    title: reportTitleSchema,
    subtitle: reportSubtitleSchema.nullable().optional(),
    depth: z.enum(["brief", "standard", "deep"]),
    language: languageSchema,
    as_of: isoDateSchema.nullable().optional(),
    sources: z.array(reportSourceHandleSchema).max(MAX_REPORT_SOURCES),
    blocks: z.array(reportBlockSchema).min(1).max(MAX_REPORT_BLOCKS),
    generation_metadata: reportGenerationMetadataSchema.nullable().optional(),
  })
  .strict();

export type ReportDefinitionV2 = z.infer<typeof reportDefinitionV2Schema>;

// The real version discriminator: every schema-v1 render_payload published in
// production must keep parsing through the v1 branch unchanged, and schema-v2
// definitions (with `image` blocks) parse through the v2 branch.
export const reportDefinitionSchema = z.discriminatedUnion("schema_version", [
  reportDefinitionV1Schema,
  reportDefinitionV2Schema,
]);

export type ReportDefinition = z.infer<typeof reportDefinitionSchema>;

export const reportMetadataPatchSchema = z
  .object({
    title: reportTitleSchema.optional(),
    subtitle: reportSubtitleSchema.nullable().optional(),
    depth: z.enum(["brief", "standard", "deep"]).optional(),
    language: languageSchema.optional(),
    as_of: isoDateSchema.nullable().optional(),
  })
  .strict();

export type ReportMetadataPatch = z.infer<typeof reportMetadataPatchSchema>;

export const replaceBlockSchema = z
  .object({
    block_id: identifierSchema,
    block: reportBlockV1Schema,
  })
  .strict();

export type ReplaceBlock = z.infer<typeof replaceBlockSchema>;

export const addBlockSchema = z
  .object({
    after_block_id: identifierSchema.nullable().optional(),
    block: reportBlockV1Schema,
  })
  .strict();

export type AddBlock = z.infer<typeof addBlockSchema>;

export const reportPatchSchema = z
  .object({
    schema_version: z.literal(REPORT_SCHEMA_VERSION),
    base_revision_number: revisionNumberSchema,
    metadata: reportMetadataPatchSchema.nullable().optional(),
    sources: z.array(reportSourceHandleSchema).max(MAX_REPORT_SOURCES).nullable().optional(),
    replace_blocks: z.array(replaceBlockSchema).max(MAX_REPORT_BLOCKS).default([]),
    add_blocks: z.array(addBlockSchema).max(MAX_REPORT_BLOCKS).default([]),
    remove_block_ids: z.array(identifierSchema).max(MAX_REPORT_BLOCKS).default([]),
    block_order: z.array(identifierSchema).max(MAX_REPORT_BLOCKS).nullable().optional(),
  })
  .strict();

export type ReportPatch = z.infer<typeof reportPatchSchema>;

export const replaceBlockV2Schema = z
  .object({
    block_id: identifierSchema,
    block: reportBlockSchema,
  })
  .strict();

export type ReplaceBlockV2 = z.infer<typeof replaceBlockV2Schema>;

export const addBlockV2Schema = z
  .object({
    after_block_id: identifierSchema.nullable().optional(),
    block: reportBlockSchema,
  })
  .strict();

export type AddBlockV2 = z.infer<typeof addBlockV2Schema>;

export const reportPatchV2Schema = z
  .object({
    schema_version: z.literal(REPORT_SCHEMA_VERSION_V2),
    base_revision_number: revisionNumberSchema,
    metadata: reportMetadataPatchSchema.nullable().optional(),
    sources: z.array(reportSourceHandleSchema).max(MAX_REPORT_SOURCES).nullable().optional(),
    replace_blocks: z.array(replaceBlockV2Schema).max(MAX_REPORT_BLOCKS).default([]),
    add_blocks: z.array(addBlockV2Schema).max(MAX_REPORT_BLOCKS).default([]),
    remove_block_ids: z.array(identifierSchema).max(MAX_REPORT_BLOCKS).default([]),
    block_order: z.array(identifierSchema).max(MAX_REPORT_BLOCKS).nullable().optional(),
  })
  .strict();

export type ReportPatchV2 = z.infer<typeof reportPatchV2Schema>;

export const anyReportPatchSchema = z.discriminatedUnion("schema_version", [
  reportPatchSchema,
  reportPatchV2Schema,
]);

export type AnyReportPatch = z.infer<typeof anyReportPatchSchema>;

export const reportSummarySchema = z
  .object({
    report_id: z.guid(),
    latest_revision_number: revisionNumberSchema,
    title: reportTitleSchema,
    subtitle: reportSubtitleSchema.nullable(),
    depth: z.enum(["brief", "standard", "deep"]),
    language: languageSchema,
    status: z.enum(["ready", "partial"]),
    updated_at: z.string().datetime({ offset: true }),
  })
  .strict();

export type ReportSummary = z.infer<typeof reportSummarySchema>;

export const reportListResponseSchema = z.array(reportSummarySchema);

export const reportRevisionSourceSchema = z
  .object({
    handle: identifierSchema,
    source_kind: z.enum([
      "knowledge_item",
      "approved_web",
      "github",
      "shortcut",
      "mongodb",
      "cloudwatch",
      "slack",
    ]),
    title: z.string().min(1),
    public_url: z.string().url().nullable(),
    preview: z.string().nullable(),
  })
  .strict();

export type ReportRevisionSource = z.infer<typeof reportRevisionSourceSchema>;

export const reportRevisionSchema = z
  .object({
    report_id: z.guid(),
    revision_number: revisionNumberSchema,
    status: z.enum(["ready", "partial"]),
    definition: reportDefinitionSchema,
    sources: z.array(reportRevisionSourceSchema).max(MAX_REPORT_SOURCES),
    // TODO(reports-images-owner-surface): the owner-authenticated revision
    // read (`GET /chat/sessions/{id}/reports/{id}/revisions/{n}` ->
    // `ReportRevisionSnapshot`, `backend/src/reports/repository.py`) does
    // not emit a visual-asset field at all yet — only the audience
    // projection used by team/public sharing
    // (`SafeReportProjection.visual_assets`) does. Optional/defaulted here
    // so today's real response (no `visual_assets` key) keeps parsing
    // unchanged; every image block in the owner view degrades to the
    // neutral "unavailable" tile until that backend gap is closed
    // separately.
    visual_assets: visualAssetListSchema,
    created_at: z.string().datetime({ offset: true }),
  })
  .strict();

export type ReportRevision = z.infer<typeof reportRevisionSchema>;

export const reportRevisionReadSchema = z
  .object({
    revision: reportRevisionSchema,
    newer_revision: reportRefSchema.nullable(),
  })
  .strict();

export type ReportRevisionRead = z.infer<typeof reportRevisionReadSchema>;

export function listSessionReports(sessionId: string): Promise<ReportSummary[]> {
  return apiClient.get(
    `/chat/sessions/${encodeURIComponent(sessionId)}/reports`,
    reportListResponseSchema,
  );
}

export function getReportRevision(args: {
  sessionId: string;
  reportId: string;
  revisionNumber: number;
}): Promise<ReportRevisionRead> {
  return apiClient.get(
    `/chat/sessions/${encodeURIComponent(args.sessionId)}/reports/${encodeURIComponent(args.reportId)}/revisions/${args.revisionNumber}`,
    reportRevisionReadSchema,
  );
}

export const shareAudienceSchema = z.enum(["private", "beflow_team", "anyone_with_link"]);
export type ShareAudience = z.infer<typeof shareAudienceSchema>;

export const safeSourceProjectionSchema = z
  .object({
    handle: identifierSchema,
    label: z.string().min(1),
    url: z.string().url().nullable(),
    redacted: z.boolean(),
    share_class: z.enum(["public", "internal"]),
  })
  .strict();

export type SafeSourceProjection = z.infer<typeof safeSourceProjectionSchema>;

export const safeReportProjectionSchema = z
  .object({
    policy_version: z.string().min(1),
    kind: z.enum(["public", "team_minimum"]),
    title: reportTitleSchema,
    subtitle: reportSubtitleSchema.nullable(),
    depth: z.enum(["brief", "standard", "deep"]),
    language: languageSchema,
    as_of: isoDateSchema.nullable(),
    status: z.enum(["ready", "partial"]),
    revision_number: revisionNumberSchema,
    definition: reportDefinitionSchema,
    sources: z.array(safeSourceProjectionSchema).max(MAX_REPORT_SOURCES),
    redacted_source_count: z.number().int().min(0),
    // Mirrors the backend `SafeReportProjection.visual_assets` field name
    // and shape exactly (see the module comment above
    // `safeVisualRelationProjectionSchema`) — a flat, block/position-ordered
    // list with no `handle`. Every audience (public/team_minimum) reuses the
    // same wire shape; only `included` and the presence of `alt_text`
    // distinguish an included relation from a neutral placeholder, so an
    // anonymous/team payload is structurally incapable of leaking owner-only
    // image metadata through this field.
    visual_assets: visualAssetListSchema,
  })
  .strict();

export type SafeReportProjection = z.infer<typeof safeReportProjectionSchema>;

export const shareGrantStateSchema = z
  .object({
    audience: shareAudienceSchema,
    grant_id: z.guid().nullable().optional(),
    token_prefix: z.string().nullable().optional(),
    expires_at: z.string().datetime({ offset: true }).nullable().optional(),
    allow_pdf: z.boolean(),
    projection_hash: z.string().nullable().optional(),
    projection_policy_version: z.string().nullable().optional(),
    redacted_source_count: z.number().int().min(0),
    has_active_token: z.boolean(),
  })
  .strict();

export type ShareGrantState = z.infer<typeof shareGrantStateSchema>;

export const sharePreviewResponseSchema = z
  .object({
    audience: shareAudienceSchema,
    redacted_source_count: z.number().int().min(0),
    projection: safeReportProjectionSchema,
    narrative_shared: z.boolean(),
    team_visibility_note: z.string().nullable().optional(),
    revoke_cannot_recall_pdf: z.boolean(),
  })
  .strict();

export type SharePreviewResponse = z.infer<typeof sharePreviewResponseSchema>;

export const shareMutationRequestSchema = z
  .object({
    audience: shareAudienceSchema,
    expires_at: z.string().datetime({ offset: true }).nullable().optional(),
    allow_pdf: z.boolean().default(false),
    replace_token: z.boolean().default(false),
  })
  .strict();

export type ShareMutationRequest = z.infer<typeof shareMutationRequestSchema>;

export const shareMutationResponseSchema = z
  .object({
    state: shareGrantStateSchema,
    capability_secret: z.string().nullable(),
    preview: sharePreviewResponseSchema,
  })
  .strict();

export type ShareMutationResponse = z.infer<typeof shareMutationResponseSchema>;

// Deliberately the exact field set the backend's `PublicProjectionAssetAccess`
// validates — the client copies one of these straight into the byte-fetch
// POST body (capability-in-body convention, never a query string). Emitted
// only for relations whose projection entry has `included: true`.
export const publicVisualReadCapabilitySchema = z
  .object({
    grant_id: z.guid(),
    relation_id: z.guid(),
    projection_hash: z.string().min(1),
    policy_version: z.string().min(1),
    rendition_identity: z.string().min(1),
    variant: z.literal("rendition"),
  })
  .strict();

export type PublicVisualReadCapability = z.infer<typeof publicVisualReadCapabilitySchema>;

export const publicResolveResponseSchema = z
  .object({
    report_id: z.guid(),
    revision_number: revisionNumberSchema,
    status: z.enum(["ready", "partial"]),
    projection: safeReportProjectionSchema,
    projection_hash: z.string().min(1),
    projection_policy_version: z.string().min(1),
    allow_pdf: z.boolean(),
    next_public_revision_available: z.boolean(),
    grant_id: z.guid(),
    visual_read_capabilities: z
      .array(publicVisualReadCapabilitySchema)
      .max(MAX_REPORT_ASSETS)
      .default([]),
  })
  .strict();

export type PublicResolveResponse = z.infer<typeof publicResolveResponseSchema>;

export const transitionMintResponseSchema = z
  .object({
    transition_token: z.string().min(1),
    expires_in_seconds: z.number().int().min(1).max(300),
  })
  .strict();

export type TransitionMintResponse = z.infer<typeof transitionMintResponseSchema>;

export const teamResolveResponseSchema = z
  .object({
    report_id: z.guid(),
    revision_number: revisionNumberSchema,
    status: z.enum(["ready", "partial"]),
    projection: safeReportProjectionSchema,
    projection_hash: z.string().min(1),
    projection_policy_version: z.string().min(1),
    allow_pdf: z.boolean(),
    grant_id: z.guid(),
  })
  .strict();

export type TeamResolveResponse = z.infer<typeof teamResolveResponseSchema>;

export const pdfExportStateSchema = z
  .object({
    export_id: z.guid(),
    status: z.enum(["queued", "rendering", "ready", "failed"]),
    projection_kind: z.enum(["owner", "team_minimum", "public"]),
    projection_hash: z.string().min(1),
    error_code: z.string().nullable().optional(),
    size_bytes: z.number().int().nullable().optional(),
  })
  .strict();

export type PdfExportState = z.infer<typeof pdfExportStateSchema>;

function shareBase(sessionId: string, reportId: string, revisionNumber: number): string {
  return `/chat/sessions/${encodeURIComponent(sessionId)}/reports/${encodeURIComponent(reportId)}/revisions/${revisionNumber}`;
}

export function getShareState(args: {
  sessionId: string;
  reportId: string;
  revisionNumber: number;
}): Promise<ShareGrantState> {
  return apiClient.get(
    `${shareBase(args.sessionId, args.reportId, args.revisionNumber)}/share`,
    shareGrantStateSchema,
  );
}

export function previewShare(args: {
  sessionId: string;
  reportId: string;
  revisionNumber: number;
  audience: ShareAudience;
}): Promise<SharePreviewResponse> {
  return apiClient.get(
    `${shareBase(args.sessionId, args.reportId, args.revisionNumber)}/share/preview`,
    sharePreviewResponseSchema,
    { query: { audience: args.audience } },
  );
}

export function setShareAudience(args: {
  sessionId: string;
  reportId: string;
  revisionNumber: number;
  body: ShareMutationRequest;
}): Promise<ShareMutationResponse> {
  return apiClient.put(
    `${shareBase(args.sessionId, args.reportId, args.revisionNumber)}/share`,
    shareMutationResponseSchema,
    args.body,
  );
}

export function revokeShare(args: {
  sessionId: string;
  reportId: string;
  revisionNumber: number;
}): Promise<ShareGrantState> {
  return apiClient.delete(
    `${shareBase(args.sessionId, args.reportId, args.revisionNumber)}/share`,
    shareGrantStateSchema,
  );
}

export function requestOwnerPdf(args: {
  sessionId: string;
  reportId: string;
  revisionNumber: number;
}): Promise<PdfExportState> {
  return apiClient.post(
    `${shareBase(args.sessionId, args.reportId, args.revisionNumber)}/pdf`,
    pdfExportStateSchema,
    {},
  );
}

export function getOwnerPdfStatus(args: {
  sessionId: string;
  reportId: string;
  revisionNumber: number;
  exportId: string;
}): Promise<PdfExportState> {
  return apiClient.get(
    `${shareBase(args.sessionId, args.reportId, args.revisionNumber)}/pdf/${encodeURIComponent(args.exportId)}`,
    pdfExportStateSchema,
  );
}

/**
 * Download owner PDF bytes with the Bearer token. Top-level navigation cannot
 * send Authorization, so callers must use this blob path instead of window.open.
 */
export async function downloadOwnerPdfBlob(args: {
  sessionId: string;
  reportId: string;
  revisionNumber: number;
  exportId: string;
}): Promise<Blob> {
  const { authHeader } = await import("./api");
  const headers: Record<string, string> = {
    Accept: "application/pdf",
    ...(await authHeader()),
  };
  const path = `${shareBase(args.sessionId, args.reportId, args.revisionNumber)}/pdf/${encodeURIComponent(args.exportId)}/download`;
  const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
  const response = await fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers,
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`pdf_download_failed_${response.status}`);
  }
  return response.blob();
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
}

const PDF_POLL_ATTEMPTS = 40;
const PDF_POLL_MS = 1500;

export async function pollUntilPdfReady(
  getStatus: () => Promise<PdfExportState>,
): Promise<PdfExportState> {
  let state = await getStatus();
  for (
    let attempt = 0;
    attempt < PDF_POLL_ATTEMPTS && state.status !== "ready" && state.status !== "failed";
    attempt += 1
  ) {
    await new Promise((resolve) => window.setTimeout(resolve, PDF_POLL_MS));
    state = await getStatus();
  }
  return state;
}

/** Public resolve — no auth header; capability only in POST body. */
export async function resolvePublicReport(args: {
  capability?: string;
  transitionToken?: string;
}): Promise<PublicResolveResponse> {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
  const response = await fetch(`${baseUrl}/shared/reports/resolve`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({
      capability: args.capability ?? null,
      transition_token: args.transitionToken ?? null,
    }),
    credentials: "omit",
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`resolve_failed_${response.status}`);
  }
  return publicResolveResponseSchema.parse(await response.json());
}

export async function mintPublicTransition(capability: string): Promise<TransitionMintResponse> {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
  const response = await fetch(`${baseUrl}/shared/reports/transition`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ capability }),
    credentials: "omit",
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`transition_failed_${response.status}`);
  }
  return transitionMintResponseSchema.parse(await response.json());
}

async function publicPdfPost(
  path: string,
  capability: string,
  expectPdf: boolean,
): Promise<Response> {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
  return fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: {
      Accept: expectPdf ? "application/pdf" : "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ capability, transition_token: null }),
    credentials: "omit",
    cache: "no-store",
  });
}

export async function requestPublicPdf(capability: string): Promise<PdfExportState> {
  const response = await publicPdfPost("/shared/reports/pdf", capability, false);
  if (!response.ok) {
    throw new Error(`pdf_failed_${response.status}`);
  }
  return pdfExportStateSchema.parse(await response.json());
}

export async function getPublicPdfStatus(
  capability: string,
  exportId: string,
): Promise<PdfExportState> {
  const response = await publicPdfPost(
    `/shared/reports/pdf/${encodeURIComponent(exportId)}/status`,
    capability,
    false,
  );
  if (!response.ok) {
    throw new Error(`pdf_status_failed_${response.status}`);
  }
  return pdfExportStateSchema.parse(await response.json());
}

export async function downloadPublicPdfBlob(capability: string, exportId: string): Promise<Blob> {
  const response = await publicPdfPost(
    `/shared/reports/pdf/${encodeURIComponent(exportId)}/download`,
    capability,
    true,
  );
  if (!response.ok) {
    throw new Error(`pdf_download_failed_${response.status}`);
  }
  return response.blob();
}

export function resolveTeamReport(args: {
  reportId: string;
  revisionNumber: number;
}): Promise<TeamResolveResponse> {
  return apiClient.get(
    `/shared/team/reports/${encodeURIComponent(args.reportId)}/revisions/${args.revisionNumber}`,
    teamResolveResponseSchema,
  );
}

export function requestTeamPdf(args: {
  reportId: string;
  revisionNumber: number;
}): Promise<PdfExportState> {
  return apiClient.post(
    `/shared/team/reports/${encodeURIComponent(args.reportId)}/revisions/${args.revisionNumber}/pdf`,
    pdfExportStateSchema,
    {},
  );
}

export function getTeamPdfStatus(args: {
  reportId: string;
  revisionNumber: number;
  exportId: string;
}): Promise<PdfExportState> {
  return apiClient.get(
    `/shared/team/reports/${encodeURIComponent(args.reportId)}/revisions/${args.revisionNumber}/pdf/${encodeURIComponent(args.exportId)}`,
    pdfExportStateSchema,
  );
}

export async function downloadTeamPdfBlob(args: {
  reportId: string;
  revisionNumber: number;
  exportId: string;
}): Promise<Blob> {
  const { authHeader } = await import("./api");
  const headers: Record<string, string> = {
    Accept: "application/pdf",
    ...(await authHeader()),
  };
  const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
  const path = `/shared/team/reports/${encodeURIComponent(args.reportId)}/revisions/${args.revisionNumber}/pdf/${encodeURIComponent(args.exportId)}/download`;
  const response = await fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers,
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`pdf_download_failed_${response.status}`);
  }
  return response.blob();
}

export function publicShareUrl(capabilitySecret: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/shared/report#${capabilitySecret}`;
}

/** Map a safe projection into the authenticated reader shape for reuse. */
export function projectionToRevisionRead(
  response: Pick<
    PublicResolveResponse | TeamResolveResponse,
    "report_id" | "revision_number" | "status" | "projection"
  >,
): ReportRevisionRead {
  return {
    revision: {
      report_id: response.report_id,
      revision_number: response.revision_number,
      status: response.status,
      definition: response.projection.definition,
      sources: response.projection.sources.map((source) => ({
        handle: source.handle,
        source_kind: "approved_web",
        title: source.label,
        public_url: source.url,
        preview: null,
      })),
      visual_assets: response.projection.visual_assets,
      created_at: new Date().toISOString(),
    },
    newer_revision: null,
  };
}
